import { z } from "zod";
import { Id } from "../common/ids.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { MessageEntry } from "./message.routes.js";
/**
 * Where one of the caller's conversations stands for them (ADR 0029): what the client compares with its own copy to
 * know what to fetch. A conversation the caller has left carries no entry and no counts; its client deletes its copy.
 */
export const ConversationSummary = z
    .object({
    conversationId: Id,
    membership: z
        .enum(["active", "left"])
        .describe("The caller's own place in it; `left` means the client deletes its copy"),
    status: z.enum(["pending", "active", "closed"]).describe("The conversation's own state"),
    lastSeq: z.number().int().nonnegative(),
    firstKeptSeq: z.number().int().positive(),
    readSeq: z.number().int().nonnegative(),
    unreadCount: z.number().int().nonnegative(),
    latest: MessageEntry.nullable().describe("The log's latest entry, for the list's preview"),
    pinnedAt: Timestamp.nullable(),
    mutedUntil: Timestamp.nullable(),
    changedAt: Timestamp,
})
    .meta({ id: "ConversationSummary" });
/** An opaque position the server made: a cursor or a page. */
const Position = z.string().regex(/^[A-Za-z0-9_-]{1,200}$/);
export const getInbox = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.inbox.get",
    method: "GET",
    path: "/v1/messaging/inbox",
    marks: ["tenant"],
    summary: "The caller's conversations in the active enterprise that changed since the last sync: follow `nextPage` until it is null, then keep `cursor` for the next sync. Without a cursor, every conversation; with one, the server also returns what changed in the minute before it, so a change that committed late is never missed",
    prd: ["IM-1"],
    request: {
        query: z.object({
            since: Position.optional().describe("The `cursor` the last sync ended with"),
            page: Position.optional().describe("The `nextPage` of the previous page of this sync"),
            limit: z.coerce.number().int().min(1).max(500).default(200),
        }),
    },
    responses: {
        200: {
            description: "A page of changed conversations, oldest change first",
            schema: z.object({
                items: z.array(ConversationSummary),
                nextPage: Position.nullable(),
                cursor: Position.describe("Kept for the next sync once `nextPage` is null"),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
/** "Until I unmute": a time no one reaches (card 158; owner, 2026-10-30). */
export const MUTED_UNTIL_UNMUTED = "9999-12-31T23:59:59.000Z";
/** A person's own settings for one of their conversations (ADR 0029, section 8; card 158). */
export const ConversationSettings = z
    .object({
    pinnedAt: Timestamp.nullable().describe("When the person first pinned it; null when it is not pinned"),
    mutedUntil: Timestamp.nullable().describe(`Muted until then, or until unmuted when ${MUTED_UNTIL_UNMUTED}; null when not muted`),
    changedAt: Timestamp.describe("The inbox time the change took: the newer wins on each device"),
})
    .meta({ id: "ConversationSettings" });
export const updateConversationSettings = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.conversations.settings",
    method: "PATCH",
    path: "/v1/conversations/{conversationId}/settings",
    marks: ["tenant"],
    summary: "Pin or unpin a conversation, or mute it, for the caller alone, from a desktop: only what the body names changes, so two devices never undo each other's change, and each of the caller's devices hears of it",
    prd: ["IM-1"],
    request: {
        params: z.object({ conversationId: Id }),
        body: z
            .object({
            pinned: z.boolean().optional(),
            mutedUntil: Timestamp.nullable()
                .optional()
                .describe(`A time ahead, or ${MUTED_UNTIL_UNMUTED} until unmuted; null unmutes`),
        })
            .refine((body) => body.pinned !== undefined || body.mutedUntil !== undefined, {
            message: "Name a setting to change",
        }),
    },
    responses: { 200: { description: "The caller's settings now", schema: ConversationSettings } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED"],
});
export const inboxRoutes = [getInbox, updateConversationSettings];
//# sourceMappingURL=inbox.routes.js.map